import { useEffect, useRef, useState } from 'react';
import { Loader2, Smartphone, Unlink, CheckCircle2, AlertTriangle } from 'lucide-react';
import './ZZP/ZZPShared.css';
import './WhatsAppKoppeling.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

// Koppelt een WhatsApp-nummer aan het dashboard (als "gekoppeld apparaat"),
// zodat de planning direct vanuit het dashboard verstuurd wordt.
export default function WhatsAppKoppeling() {
  const [st, setSt] = useState(null);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [showQr, setShowQr] = useState(false);
  const qrRef = useRef('');

  const load = async () => {
    try {
      const res = await authFetch('/api/whatsapp/status');
      const data = await res.json();
      setSt(data);
      return data;
    } catch {
      setSt({ state: 'offline' });
      return null;
    }
  };

  useEffect(() => { load(); }, []);

  // Tijdens het koppelen elke 3 s kijken of de telefoon de code heeft geaccepteerd.
  useEffect(() => {
    if (st?.state !== 'pairing') return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [st?.state]);

  // QR-afbeelding met auth ophalen (een gewone <img src> stuurt geen token mee).
  useEffect(() => {
    if (!showQr || st?.state !== 'pairing' || !st?.hasQr) return;
    authFetch(`/api/whatsapp/qr?t=${st.t}`).then((r) => (r.ok ? r.blob() : null)).then((b) => {
      if (!b) return;
      if (qrRef.current) URL.revokeObjectURL(qrRef.current);
      qrRef.current = URL.createObjectURL(b);
      setQrUrl(qrRef.current);
    }).catch(() => {});
  }, [showQr, st?.state, st?.t, st?.hasQr]);

  const pair = async (e) => {
    e.preventDefault();
    setError('');
    setBusy('pair');
    try {
      const res = await authFetch('/api/whatsapp/pair', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Koppelcode maken mislukt');
      setSt(data);
    } catch (err) { setError(err.message); }
    setBusy('');
  };

  const unlink = async () => {
    if (!confirm('WhatsApp ontkoppelen? Daarna kan de planning niet meer automatisch verstuurd worden tot je opnieuw koppelt.')) return;
    setError('');
    setBusy('unlink');
    try {
      const res = await authFetch('/api/whatsapp/unlink', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ontkoppelen mislukt');
      setSt(data);
    } catch (err) { setError(err.message); }
    setBusy('');
  };

  const state = st?.state;
  const fmtCode = (c) => (c && c.length === 8 && !c.includes('-') ? `${c.slice(0, 4)}-${c.slice(4)}` : c);

  return (
    <div className="zzp-page">
      <div className="zzp-header">
        <div>
          <h2>WhatsApp</h2>
          <p>Koppel een WhatsApp-nummer om de planning direct vanuit het dashboard te versturen</p>
        </div>
      </div>

      <div className="wa-card">
        {!st && <div className="wa-row"><Loader2 size={18} className="wa-spin" /> Status laden…</div>}

        {state === 'unavailable' && (
          <div className="wa-row wa-warn"><AlertTriangle size={18} /> De WhatsApp-dienst draait niet op deze server.</div>
        )}

        {state === 'offline' && (
          <div className="wa-row wa-warn"><AlertTriangle size={18} /> De WhatsApp-dienst start opnieuw op. Probeer het over een minuut nog eens.</div>
        )}

        {state === 'linked' && (
          <>
            <div className="wa-row wa-ok"><CheckCircle2 size={20} /> Gekoppeld met <strong>+{st.user}</strong></div>
            <p className="wa-muted">De groene WhatsApp-knop in de Planning verstuurt het rooster nu direct naar de medewerker.</p>
            <button className="wa-btn-outline" onClick={unlink} disabled={busy === 'unlink'}>
              {busy === 'unlink' ? <Loader2 size={16} className="wa-spin" /> : <Unlink size={16} />} Ontkoppelen
            </button>
          </>
        )}

        {state === 'unlinked' && (
          <>
            <div className="wa-row"><Smartphone size={20} /> Niet gekoppeld</div>
            {st.error && <div className="wa-row wa-warn"><AlertTriangle size={16} /> {st.error}</div>}
            <form onSubmit={pair} className="wa-form">
              <div className="zzp-form-group">
                <label htmlFor="wa-phone">WhatsApp-nummer van het bedrijf</label>
                <input id="wa-phone" type="tel" inputMode="tel" placeholder="06 12345678" value={phone}
                  onChange={(e) => { setPhone(e.target.value); setError(''); }} />
              </div>
              <button type="submit" className="zzp-btn-submit" disabled={!phone.trim() || busy === 'pair'}>
                {busy === 'pair' ? <><Loader2 size={16} className="wa-spin" /> Code maken…</> : 'Koppelcode maken'}
              </button>
            </form>
          </>
        )}

        {state === 'pairing' && (
          <>
            <div className="wa-row"><Loader2 size={18} className="wa-spin" /> Wachten op de telefoon van +{st.phone}…</div>
            {st.code ? (
              <>
                <div className="wa-code">{fmtCode(st.code)}</div>
                <ol className="wa-steps">
                  <li>Open WhatsApp op de telefoon van <strong>+{st.phone}</strong></li>
                  <li>Instellingen → <strong>Gekoppelde apparaten</strong> → <strong>Apparaat koppelen</strong></li>
                  <li>Tik onderaan op <strong>Koppelen met telefoonnummer</strong></li>
                  <li>Typ de code hierboven</li>
                </ol>
                <p className="wa-muted">De code is een paar minuten geldig. Deze pagina ziet vanzelf wanneer het gelukt is.</p>
              </>
            ) : (
              <p className="wa-muted">Code wordt gemaakt…</p>
            )}
            {st.hasQr && (
              <button type="button" className="wa-link" onClick={() => setShowQr((v) => !v)}>
                {showQr ? 'QR-code verbergen' : 'Liever een QR-code scannen?'}
              </button>
            )}
            {showQr && qrUrl && <img className="wa-qr" src={qrUrl} alt="QR-code om WhatsApp te koppelen" />}
          </>
        )}

        {error && <div className="zzp-form-error">{error}</div>}
      </div>

      <div className="wa-note">
        <strong>Let op:</strong> verstuur alleen naar je eigen medewerkers en niet in grote aantallen tegelijk.
        WhatsApp kan een nummer blokkeren als het op spam lijkt. Wordt de koppeling verbroken (bijvoorbeeld als de
        telefoon twee weken offline is), maak dan hier een nieuwe koppelcode.
      </div>
    </div>
  );
}
